import { useCallback, useEffect, useEffectEvent, useRef, useState, type ReactNode } from 'react';
import { mapbox, type mapboxgl } from '../../shared/results_page/mapbox_module.js';
import {
  liveMap,
  mapAnchor,
  MapViewControls,
  useMapboxContainerResize,
  useMapboxInstance,
  useMapInitializationFailure,
  type MapboxInstance,
  validMapCoordinates,
  type MapCoordinates,
  type MapViewControlsCopy,
} from '../../shared/results_page/results_map.js';
import { fitPadding, overlaysKey, type MapOverlays } from '../../shared/results_page/map_fit.js';
import { useKeyedValue } from '../../shared/use_keyed_value.js';
import { useStableCallback } from '../../shared/use_stable_callback.js';
import {
  MAP_SEARCH_AREA_TOP_FALLBACK,
  renderMapSearchArea,
  useMapSearchArea,
  type MapSearchArea,
  type MapSearchAreaCopy,
} from '../../shared/results_page/map_search_area.js';
import type { RentalMapView } from '../models.js';
import {
  CLUSTER_MAX_ZOOM,
  clusterRentalPins,
  type RentalMapCluster,
} from './rental_map_clusters.js';
import { agencyPin, clusterPin, type RentalMapPinCopy } from './rental_map_markers.js';
import { rentalAgencyPins, type RentalAgencyPin, type RentalMapOffer } from './rental_map_pins.js';
import './rental_map.css';

export type { RentalMapPinCopy } from './rental_map_markers.js';

/** O "buscar nesta área" do mapa do carro (#3995): o gesto entrega a área ao
 *  host, que a troca por um lugar e refaz a busca. `maxRadiusKm` é o teto do
 *  fornecedor: acima dele o botão vira o pedido de aproximar (`copy.zoomInLabel`).
 *  A promessa segura o "Buscando…" do botão até a lista nova chegar. */
export type RentalMapSearchArea = {
  onSearchArea: (area: MapSearchArea) => void | Promise<void>;
  copy: MapSearchAreaCopy;
  maxRadiusKm?: number;
};

export type RentalMapProps = {
  map: RentalMapView;
  /** As ofertas da cena do mapa, já cortadas e ordenadas: cada agência de
   *  retirada com coordenada vira um pino (#3650). Ausente ⇒ só a âncora. */
  offers?: readonly RentalMapOffer[];
  /** O card selecionado — o pino da agência de retirada dele destaca. */
  selectedOfferId?: string;
  /** O pino que filtra a lista (`RentalAgencyPin.key`): destaca e fica
   *  pressionado (`aria-pressed`). */
  pinnedKey?: string;
  /** O toque no pino. Presente, o pino é BOTÃO; ausente, é só leitura. */
  onPinPress?: (pin: RentalAgencyPin) => void;
  /** O que o chamador desenha por cima do mapa (a folha do palco estreito):
   *  entra no enquadramento para os pinos não pararem atrás dela. */
  overlays?: MapOverlays;
  /** O preço e o nome acessível do pino. Sem ele o pino diz só a locadora. */
  pinCopy?: RentalMapPinCopy;
  controls?: MapViewControlsCopy;
  /** Ausente ⇒ o mapa não oferece buscar na área visível. */
  searchArea?: RentalMapSearchArea;
};

/** O zoom de CIDADE sobre o lugar buscado — o mesmo do hotel quando só há a
 *  âncora em cena: um ponto só não tem caixa para enquadrar. */
const ANCHOR_ZOOM = 13;

/** O teto do enquadramento com pinos: uma agência só ao lado da âncora não
 *  leva a câmera ao nível da rua. */
const MAP_FIT_MAX_ZOOM = 15;

/** Depois do primeiro enquadramento a câmera VIAJA — o corte seco desorienta
 *  quando ela já está em algum lugar (a lista mudou com um filtro). */
const MAP_CAMERA_EASE_MS = 700;

const NO_OFFERS: readonly RentalMapOffer[] = [];
const NO_OVERLAYS: MapOverlays = {};

/** A âncora só chega ao marcador com coordenada que o Mapbox aceita
 *  (`validMapCoordinates`); fora disso o mapa cai no fallback. */
function anchorCoordinates(anchor: RentalMapView['anchor']): MapCoordinates | undefined {
  return validMapCoordinates(anchor?.coordinates);
}

/** A âncora do lugar buscado como marcador do Mapbox — nasce com o mapa e some
 *  com ele; muda de lugar quando a busca muda. */
function useAnchorMarker({
  canRenderMap,
  mapRef,
  instance,
  anchor,
}: {
  readonly canRenderMap: boolean;
  readonly mapRef: { readonly current: mapboxgl.Map | null };
  readonly instance: MapboxInstance | null;
  readonly anchor: RentalMapView['anchor'];
}): void {
  const coordinates = anchorCoordinates(anchor);
  const latitude = coordinates?.latitude;
  const longitude = coordinates?.longitude;
  const label = anchor?.label;
  useEffect(() => {
    const mapInstance = liveMap(mapRef, instance);
    if (
      !canRenderMap ||
      !mapInstance ||
      latitude === undefined ||
      longitude === undefined ||
      !label
    ) {
      return undefined;
    }
    const marker = new (mapbox().Marker)({ element: mapAnchor(label) })
      .setLngLat([longitude, latitude])
      .addTo(mapInstance);
    return () => {
      marker.remove();
    };
    // `instance` acorda o efeito quando a instância é recriada; lat/lng entram
    // como primitivos porque o objeto de coordenadas chega novo a cada render.
  }, [canRenderMap, instance, latitude, longitude, label, mapRef]);
}

/** A chave do pino selecionado: o da agência de retirada do card escolhido. */
function selectedPinKey(
  pins: readonly RentalAgencyPin[],
  selectedOfferId: string | undefined,
): string | undefined {
  if (!selectedOfferId) return undefined;
  return pins.find((pin) => pin.offerIds.includes(selectedOfferId))?.key;
}

/** O zoom da câmera, lido no `zoomend`: é dele que sai o agrupamento dos
 *  pinos (`clusterRentalPins`) — arrastar não muda a distância entre eles na
 *  tela, aproximar e afastar mudam. */
function useMapZoom({
  canRenderMap,
  mapRef,
  instance,
}: {
  readonly canRenderMap: boolean;
  readonly mapRef: { readonly current: mapboxgl.Map | null };
  readonly instance: MapboxInstance | null;
}): number {
  const [zoom, setZoom] = useState(ANCHOR_ZOOM);
  useEffect(() => {
    const mapInstance = liveMap(mapRef, instance);
    if (!canRenderMap || !mapInstance) return undefined;
    const read = () => {
      setZoom(mapInstance.getZoom());
    };
    read();
    mapInstance.on('zoomend', read);
    return () => {
      mapInstance.off('zoomend', read);
    };
  }, [canRenderMap, instance, mapRef]);
  return zoom;
}

/** Destaca o pino que contém a chave: o agrupador destaca se um dos seus pinos
 *  é o do card em checkout ou o que filtra. */
function holds(cluster: RentalMapCluster, key: string | undefined): boolean {
  return key !== undefined && cluster.pins.some((pin) => pin.key === key);
}

function clusterByKey(
  clusters: readonly RentalMapCluster[],
  key: string,
): RentalMapCluster | undefined {
  return clusters.find((candidate) => candidate.key === key);
}

/** Os pinos (e os agrupadores) como marcadores — refeitos quando o agrupamento
 *  ou a lista em cena muda (filtro, ordem, página nova, zoom que agrupa ou
 *  separa) ou a instância é recriada. O DESTAQUE (o card em checkout e o pino
 *  que filtra, os dois com o mesmo visual) é atributo no pino que já existe:
 *  remontar o marcador para destacar um faria a pílula piscar a cada toque. */
function paintPins(
  elements: ReadonlyMap<string, { element: HTMLElement; cluster: RentalMapCluster }>,
  selectedKey: string | undefined,
  pinnedKey: string | undefined,
): void {
  elements.forEach(({ element, cluster }) => {
    element.dataset.selected = String(holds(cluster, selectedKey) || holds(cluster, pinnedKey));
    if (element instanceof HTMLButtonElement && cluster.pins.length === 1) {
      element.setAttribute('aria-pressed', String(holds(cluster, pinnedKey)));
    }
  });
}

function useAgencyPins({
  canRenderMap,
  mapRef,
  instance,
  clusters,
  selectedKey,
  pinnedKey,
  pinCopy,
  onPinPress,
  onClusterPress,
}: {
  readonly canRenderMap: boolean;
  readonly mapRef: { readonly current: mapboxgl.Map | null };
  readonly instance: MapboxInstance | null;
  readonly clusters: readonly RentalMapCluster[];
  readonly selectedKey: string | undefined;
  readonly pinnedKey: string | undefined;
  readonly pinCopy: RentalMapPinCopy | undefined;
  readonly onPinPress: ((pin: RentalAgencyPin) => void) | undefined;
  readonly onClusterPress: (cluster: RentalMapCluster) => void;
}): void {
  const elementsRef = useRef(
    new Map<string, { element: HTMLElement; cluster: RentalMapCluster }>(),
  );
  // A lista é escalar de propósito: `clusters` nasce novo a cada render, e
  // como dependência crua remontaria os marcadores por quadro.
  const mountedClusters = useKeyedValue(
    clusters,
    JSON.stringify(
      clusters.map(({ key, fromPrice, pins }) => [
        key,
        fromPrice,
        pins.map((pin) => [pin.company, pin.fromPrice, pin.offerIds.length]),
      ]),
    ),
  );
  // O toque lê o handler e o pino de AGORA: as ofertas do pino mudam sem mudar
  // a assinatura (uma página nova troca uma oferta por outra da mesma agência).
  const press = useStableCallback((clusterKey: string, lone: boolean) => {
    const current = clusterByKey(clusters, clusterKey);
    const [pin] = current?.pins ?? [];
    if (!current || !pin) return;
    if (lone) onPinPress?.(pin);
    else onClusterPress(current);
  });
  // The pins born below take the current highlight; the effect after it repaints
  // the ones already on the map when the highlight moves.
  const paintCurrent = useEffectEvent(() => {
    paintPins(elementsRef.current, selectedKey, pinnedKey);
  });
  const pressable = onPinPress !== undefined;
  useEffect(() => {
    const mapInstance = liveMap(mapRef, instance);
    if (!canRenderMap || !mapInstance) return undefined;
    const elements = elementsRef.current;
    // O agrupador senta no pino que o semeou (o 1º da lista): é um ponto real
    // do mapa, não um centro inventado entre agências.
    const markers = mountedClusters.flatMap((cluster) => {
      const [seed, ...others] = cluster.pins;
      if (!seed) return [];
      const lone = others.length === 0;
      const element = lone ? agencyPin(seed, pinCopy, pressable) : clusterPin(cluster, pinCopy);
      element.addEventListener('click', (event) => {
        // O clique é do pino, não do mapa por baixo.
        event.stopPropagation();
        press(cluster.key, lone);
      });
      elements.set(cluster.key, { element, cluster });
      const { latitude, longitude } = seed.coordinates;
      return [
        new (mapbox().Marker)({ element }).setLngLat([longitude, latitude]).addTo(mapInstance),
      ];
    });
    paintCurrent();
    return () => {
      markers.forEach((marker) => marker.remove());
      elements.clear();
    };
  }, [canRenderMap, instance, mapRef, mountedClusters, pinCopy, press, pressable]);
  useEffect(() => {
    paintPins(elementsRef.current, selectedKey, pinnedKey);
  }, [selectedKey, pinnedKey]);
}

/**
 * A câmera enquadra a ÂNCORA E OS PINOS: com uma agência ou mais em cena a
 * caixa de todos os pontos cabe na tela (com a folga do pino e do que o
 * chamador desenha por cima); só com a âncora a câmera vai até ela no zoom de
 * cidade — um ponto só não tem caixa. Reenquadra quando a lista em cena muda,
 * quando o contêiner assenta depois de um resize (a divisória da página) e
 * quando a instância é recriada; devolve o `refit` do botão "reenquadrar".
 */
function useRentalCamera({
  canRenderMap,
  containerRef,
  mapRef,
  instance,
  anchor,
  pins,
  overlays,
  keepFrame,
}: {
  readonly canRenderMap: boolean;
  readonly containerRef: { readonly current: HTMLDivElement | null };
  readonly mapRef: { readonly current: mapboxgl.Map | null };
  readonly instance: MapboxInstance | null;
  readonly anchor: MapCoordinates | undefined;
  readonly pins: readonly RentalAgencyPin[];
  readonly overlays: MapOverlays;
  /** Ver `RentalMapView.keepFrame`. Lido quando a câmera enquadra: virar não
   *  reenquadra nada. */
  readonly keepFrame: boolean;
}): { refit: () => void; onResize: () => void } {
  const points = useKeyedValue<[number, number][]>([
    ...(anchor ? [[anchor.longitude, anchor.latitude] as [number, number]] : []),
    ...pins.map((pin) => [pin.coordinates.longitude, pin.coordinates.latitude] as [number, number]),
  ]);
  const frameOverlays = useKeyedValue(overlays, overlaysKey(overlays));
  const readKeepFrame = useEffectEvent(() => keepFrame);
  /* O enquadramento corrente, para o "Reenquadrar" repetir; `liveFitRef` só
     enquanto o efeito que o criou está de pé, para o resize assentado. */
  const fitRef = useRef<((animated: boolean) => void) | null>(null);
  const liveFitRef = useRef<((animated: boolean) => void) | null>(null);
  // The instance already framed once: its next frame travels (animated).
  const framedRef = useRef<MapboxInstance | null>(null);
  useEffect(() => {
    const mapInstance = liveMap(mapRef, instance);
    const container = containerRef.current;
    const [first, ...rest] = points;
    if (!canRenderMap || !mapInstance || !container || !first) return undefined;
    const fit = (animated: boolean) => {
      if (rest.length === 0) {
        mapInstance.easeTo({ center: first, zoom: ANCHOR_ZOOM });
        return;
      }
      const bounds = rest.reduce(
        (box, point) => box.extend(point),
        new (mapbox().LngLatBounds)(first, first),
      );
      mapInstance.fitBounds(bounds, {
        padding: fitPadding(container, frameOverlays),
        maxZoom: MAP_FIT_MAX_ZOOM,
        duration: animated ? MAP_CAMERA_EASE_MS : 0,
      });
    };
    const animated = framedRef.current === instance;
    framedRef.current = instance;
    fitRef.current = fit;
    liveFitRef.current = fit;
    const release = () => {
      if (liveFitRef.current === fit) liveFitRef.current = null;
    };
    if (animated && readKeepFrame()) return release;
    // Duas vezes, como no hotel: só depois do `load` o mapa conhece o tamanho
    // real do contêiner, e enquadrar com a medida velha deixa pino de fora.
    fit(animated);
    const onLoad = () => {
      fit(false);
    };
    mapInstance.on('load', onLoad);
    return () => {
      release();
      mapInstance.off('load', onLoad);
    };
  }, [canRenderMap, containerRef, frameOverlays, instance, mapRef, points]);
  const refit = useCallback(() => {
    fitRef.current?.(true);
  }, []);
  // O contêiner assentou num tamanho novo: reenquadra como a lista nova, menos
  // quando a câmera é da pessoa (`keepFrame`).
  const onResize = useCallback(() => {
    if (!keepFrame) liveFitRef.current?.(true);
  }, [keepFrame]);
  return { refit, onResize };
}

/**
 * O mapa da página de resultados do carro (#2441): o lugar buscado como âncora,
 * um pino por AGÊNCIA de retirada com coordenada (#3650) e os controles de
 * vista. A instância, a âncora, a folga do enquadramento e os controles são os
 * mesmos do mapa do hotel (`shared/results_page`); o que é do carro é a
 * composição e o pino por agência.
 */
export function RentalMap({
  map,
  offers = NO_OFFERS,
  selectedOfferId,
  pinnedKey,
  onPinPress,
  overlays = NO_OVERLAYS,
  pinCopy,
  controls,
  searchArea: searchAreaProp,
}: Readonly<RentalMapProps>): ReactNode {
  const containerRef = useRef<HTMLDivElement>(null);
  const accessToken = map.accessToken?.trim();
  const [initializationFailed, setInitializationFailed] = useMapInitializationFailure(
    JSON.stringify([map.available, accessToken]),
  );
  const coordinates = anchorCoordinates(map.anchor);
  const canRenderMap =
    map.available && Boolean(accessToken) && !initializationFailed && coordinates !== undefined;
  const { mapRef, instance } = useMapboxInstance({
    accessToken,
    canRenderMap,
    interactive: true,
    nativeControls: controls === undefined,
    containerRef,
    initialCoordinates: coordinates,
    initialZoom: ANCHOR_ZOOM,
    setInitializationFailed,
  });
  const searchArea = useMapSearchArea({
    canRenderMap,
    mapRef,
    instance,
    pending: false,
    ...(searchAreaProp
      ? {
          onSearchArea: searchAreaProp.onSearchArea,
          ...(searchAreaProp.maxRadiusKm === undefined
            ? {}
            : { maxRadiusKm: searchAreaProp.maxRadiusKm }),
        }
      : {}),
  });
  const pins = rentalAgencyPins(offers);
  const zoom = useMapZoom({ canRenderMap, mapRef, instance });
  useAnchorMarker({ canRenderMap, mapRef, instance, anchor: map.anchor });
  useAgencyPins({
    canRenderMap,
    mapRef,
    instance,
    clusters: clusterRentalPins(pins, zoom),
    selectedKey: selectedPinKey(pins, selectedOfferId),
    pinnedKey,
    pinCopy,
    onPinPress,
    // O toque no agrupador aproxima até a caixa dos seus pinos — no máximo até
    // o zoom em que nada mais agrupa, então eles sempre se separam.
    onClusterPress: (cluster) => {
      const mapInstance = mapRef.current;
      const container = containerRef.current;
      const [first, ...rest] = cluster.pins.map(
        ({ coordinates }) => [coordinates.longitude, coordinates.latitude] as [number, number],
      );
      if (!mapInstance || !container || !first) return;
      mapInstance.fitBounds(
        rest.reduce((box, point) => box.extend(point), new (mapbox().LngLatBounds)(first, first)),
        {
          padding: fitPadding(container, overlays),
          maxZoom: CLUSTER_MAX_ZOOM,
          duration: MAP_CAMERA_EASE_MS,
        },
      );
    },
  });
  const { refit, onResize } = useRentalCamera({
    canRenderMap,
    containerRef,
    mapRef,
    instance,
    anchor: coordinates,
    pins,
    overlays,
    keepFrame: map.keepFrame === true,
  });
  useMapboxContainerResize({ canRenderMap, containerRef, mapRef, instance, onSettle: onResize });

  if (!canRenderMap) {
    return (
      <section className="iuk-results-map-fallback" aria-label={map.fallbackLabel}>
        <p>{map.fallbackLabel}</p>
      </section>
    );
  }
  return (
    <section className="iuk-results-map" aria-label={map.label}>
      <div ref={containerRef} className="iuk-results-map-canvas" />
      {controls && (
        <MapViewControls
          controls={controls}
          // `+`/`−` são gesto da pessoa, mas o `moveend` deles não traz
          // `originalEvent`: o gesto é marcado à mão, como no hotel.
          onZoomIn={() => {
            mapRef.current?.zoomIn();
            searchArea.markGesture();
          }}
          onZoomOut={() => {
            mapRef.current?.zoomOut();
            searchArea.markGesture();
          }}
          onRecenter={refit}
        />
      )}
      {renderMapSearchArea({
        searchArea,
        copy: searchAreaProp?.copy,
        topOffset: MAP_SEARCH_AREA_TOP_FALLBACK,
      })}
    </section>
  );
}
